import { capitalize } from "lodash-es";
import queryString from "query-string";
import { shareQueryParams } from "../AppView/shareQueryParams";
import {
  DEFAULT_TIME_SCALE,
  timeScales,
  timeScaleValues,
  type TimeScale,
} from "./Widgets/Trends/timeScales";
import { daySuffix, type SelectOption } from "@SeemplicityDev/shared";
import { DateTime } from "luxon";
import {
  TEXT_WIDGET_DEFAULT_HEIGHT,
  isTextWidgetChartType,
} from "./Widgets/TextWidget/TextWidgetUtils";
import type {
  DashboardActionTarget,
  DashboardGroupItem,
  DashboardItem,
  DashboardSection,
  DashboardSectionInput,
  DashboardSectionKind,
  DashboardVisibility,
  WidgetDefinition,
} from "./DashboardTypes";

export const DASHBOARD_SECTION_KINDS = {
  USER: "USER",
  GENERAL: "GENERAL",
  SEEMPLICITY: "SEEMPLICITY",
} as const satisfies Record<string, DashboardSectionKind>;

export const DASHBOARD_VISIBILITIES = {
  PRIVATE: "PRIVATE",
  SHARED: "SHARED",
} as const satisfies Record<string, DashboardVisibility>;

/**
 * Where a section's group sits relative to the others. Everything a user made
 * sorts alphabetically among itself; the two platform-owned kinds are pinned
 * below it, General above Seemplicity.
 */
const SECTION_KIND_ORDER: Record<DashboardSectionKind, number> = {
  USER: 0,
  GENERAL: 1,
  SEEMPLICITY: 2,
};

export const compareSections = (
  a: DashboardSection,
  b: DashboardSection
): number =>
  SECTION_KIND_ORDER[a.kind] - SECTION_KIND_ORDER[b.kind] ||
  a.name.localeCompare(b.name);

export const getDashboardItemIcon = (
  item: DashboardItem
): string | undefined => {
  if (item.is_default) {
    return "seem-icon-default";
  }
  return item.visibility === DASHBOARD_VISIBILITIES.PRIVATE
    ? "seem-icon-user"
    : undefined;
};

export const formatDashboardCount = (count: number): string =>
  count === 1 ? "1 dashboard" : `${count} dashboards`;

export const SECTIONS_LOAD_ERROR_MESSAGE = "Failed to load sections";

// Sections are organization-wide, so the backend only mints one for a dashboard
// the whole organization can see.
export const canCreateSectionForVisibility = (
  visibility?: DashboardVisibility
): boolean => visibility === DASHBOARD_VISIBILITIES.SHARED;

export const DASHBOARD_NAME_CONFLICT = "Dashboard name conflict";

export const getNewWidgetHeight = ({
  min_height,
  chart_type_id,
}: Pick<WidgetDefinition, "min_height" | "chart_type_id">): number =>
  isTextWidgetChartType(chart_type_id)
    ? Math.max(TEXT_WIDGET_DEFAULT_HEIGHT, min_height)
    : min_height;

export const DEFAULT_DASHBOARD_TYPES = {
  ORGANIZATIONAL: { path: "organizational", title: "Organizational" },
  SLA: { path: "sla-compliance", title: "SLA Compliance" },
  TRENDS: { path: "trends", title: "Trends" },
  INVENTORY: { path: "inventory", title: "Inventory" },
  CLOUD_SECURITY: { path: "cloud-security", title: "Cloud Security" },
  APPLICATION_SECURITY: {
    path: "application-security",
    title: "Application Security",
  },
  VULNERABILITY_MANAGEMENT: {
    path: "vulnerability-management",
    title: "Vulnerability Management",
  },
  SECURITY_POSTURE: {
    path: "security-posture",
    title: "Security Posture Overview",
  },
  REMEDIATION: { path: "remediation", title: "Remediation & SLA Compliance" },
} as const;

export { timeScales };

const selectableTimeScales: TimeScale[] = timeScaleValues.map(
  ({ value }) => value
);

export const getTimeScaleFromSearch = (search: string): TimeScale => {
  const value = queryString.parse(search)[shareQueryParams.TIME_SCALE];
  return (
    selectableTimeScales.find((scale) => scale === value) ?? DEFAULT_TIME_SCALE
  );
};

// NOTE: the third parameter is dead — consumers (ExpectedWorkload.tsx) pass an
// `index` argument that the function body never reads. Signature preserved as-is
// to keep existing call sites valid; flagged as a refactor candidate.
export const getFormatByTimescale = (
  timeScale: string,
  luxonDate: DateTime,
  _index?: number
): string => {
  const startOfCurrentQuarter = DateTime.utc().startOf("quarter");
  const startOfNextQuarter = startOfCurrentQuarter.plus({
    [timeScalesData.QUARTER.luxonMathematicalUnit]: 1,
  });
  switch (timeScale) {
    case timeScales.DAY:
    case timeScales.WEEK: {
      return luxonDate.toLocaleString({
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
    }

    case timeScales.MONTH:
      return luxonDate?.toLocaleString({ month: "long", year: "numeric" });

    case timeScales.QUARTER: {
      const quarter = luxonDate?.quarter;
      return `${quarter}${daySuffix(
        quarter
      )} Quarter ${luxonDate?.toLocaleString({ year: "numeric" })} ${
        luxonDate >= startOfCurrentQuarter && luxonDate < startOfNextQuarter
          ? "(Current)"
          : ""
      }`;
    }

    default:
      return "";
  }
};

export const timeScalesData = {
  DAY: {
    count: 12,
    luxonPeriod: "day",
    luxonMathematicalUnit: "days",
  },
  WEEK: {
    count: 12,
    luxonPeriod: "week",
    luxonMathematicalUnit: "weeks",
  },
  MONTH: {
    count: 12,
    luxonPeriod: "month",
    luxonMathematicalUnit: "months",
  },
  QUARTER: {
    count: 3,
    luxonPeriod: "quarter",
    luxonMathematicalUnit: "quarters",
  },
} as const;

export const sortNames = (
  menuItems: DashboardGroupItem[]
): DashboardGroupItem[] => {
  const sorted: DashboardGroupItem[] = [];
  const defaults: string[] = Object.values(DEFAULT_DASHBOARD_TYPES).map(
    (i) => i.title
  );

  menuItems?.forEach((main) => {
    const untitledDashboards: DashboardItem[] = [];
    const defaultDashboards: DashboardItem[] = [];
    const userDashboards: DashboardItem[] = [];

    main?.items?.forEach((item) => {
      if (item.name.startsWith("Untitled")) {
        untitledDashboards.push(item);
      } else if (defaults.includes(item.name)) {
        defaultDashboards.push(item);
      } else {
        userDashboards.push(item);
      }
    });

    untitledDashboards.sort((a, b) => {
      const suffixA = parseInt(a.name.replace("Untitled", "").trim());
      const suffixB = parseInt(b.name.replace("Untitled", "").trim());
      return suffixA - suffixB;
    });

    userDashboards.sort((a, b) => a.name.localeCompare(b.name));

    const sortedMainItems = [
      ...untitledDashboards,
      ...defaultDashboards,
      ...userDashboards,
    ];

    sorted.push({ ...main, items: sortedMainItems });
  });

  return sorted;
};

export const getTimeScaleValue = (str: string, timeScale: string): string => {
  return str.replace(/\${(.*?)}/g, (match: string, variable: string) => {
    const value = variable.trim();
    return value === "timeScale" ? capitalize(timeScale) : match;
  });
};

export const dashboardQueries = [
  "getDashboard",
  "getTrend",
  "scopes",
  "GetFindingCounts",
  "resources_count",
  "getScopesSummary",
  "GetScopeToResourceCount",
  "getTopPackagesData",
  "getTopReposData",
  "getTopAccountsData",
  "getInventoryFindingsByDatasources",
  "getInventoryCloudAccounts",
  "getInventoryCodeRepositories",
  "getQueueSummary",
];

export const getMoveDisabledTooltip = (
  dashboard: DashboardActionTarget
): string =>
  !dashboard.is_editable ||
  dashboard.section?.kind === DASHBOARD_SECTION_KINDS.SEEMPLICITY
    ? "Dashboard in the Seemplicity section can't be moved"
    : "You don't have permission to move this dashboard";

export const getDeleteDisabledTooltip = (
  dashboard: DashboardActionTarget
): string =>
  !dashboard.is_editable ||
  dashboard.section?.kind === DASHBOARD_SECTION_KINDS.SEEMPLICITY
    ? "Dashboard in the Seemplicity section can't be deleted"
    : "You don't have permission to delete this dashboard";

export const SYSTEM_DASHBOARD_NAMES = [
  "Security Posture Overview",
  "Organizational",
] as const;

export const getPromotedDefaultDashboardName = (
  dashboards: DashboardItem[],
  deletedIds: number[]
): string | undefined =>
  SYSTEM_DASHBOARD_NAMES.find((name) =>
    dashboards.some(
      (dashboard) =>
        dashboard.name === name && !deletedIds.includes(dashboard.id)
    )
  );

export const MAX_SECTION_NAME_LENGTH = 30;
export const SECTION_NAME_REQUIRED_MESSAGE = "Please enter a section name";
const RESERVED_SECTION_NAME = "Seemplicity";
const SECTION_NAME_RESERVED_MESSAGE = `Can't create a new section called ${RESERVED_SECTION_NAME}`;
const SECTION_NAME_TOO_LONG_MESSAGE = `Maximum section name length is ${MAX_SECTION_NAME_LENGTH} characters`;

export const DASHBOARD_SECTION_NAME_CONFLICT =
  "Dashboard section name conflict";

export const validateDashboardSectionName = (value: string): true | string => {
  const name = value.trim();
  if (!name) {
    return SECTION_NAME_REQUIRED_MESSAGE;
  }
  if (name.toLowerCase() === RESERVED_SECTION_NAME.toLowerCase()) {
    return SECTION_NAME_RESERVED_MESSAGE;
  }
  if (name.length > MAX_SECTION_NAME_LENGTH) {
    return SECTION_NAME_TOO_LONG_MESSAGE;
  }
  return true;
};

export const validateDashboardSectionOption = (
  section?: SelectOption | null
): true | string =>
  section?.isNew ? validateDashboardSectionName(String(section.value)) : true;

export const toDashboardSectionInput = (
  section?: SelectOption | null
): DashboardSectionInput => {
  if (!section) {
    return {};
  }

  return section.isNew
    ? { section_name: String(section.value).trim() }
    : { section_id: Number(section.value) };
};

const DUPLICATE_NAME_SUFFIX = " - copy";

export const toDuplicateDashboardName = (name: string): string =>
  `${name}${DUPLICATE_NAME_SUFFIX}`;

export const toDashboardSectionOption = (
  section?: DashboardSection
): SelectOption | null =>
  !section || section.kind === DASHBOARD_SECTION_KINDS.SEEMPLICITY
    ? null
    : { label: section.name, value: section.id };

// A created option's `label` is the create row text, so the name the user
// actually typed only survives on `value`.
export const getDashboardSectionLabel = (section: SelectOption): string =>
  section.isNew ? String(section.value).trim() : String(section.label);

export const isDashboardNameAvailable = (
  name: string,
  groups: DashboardGroupItem[] | undefined
): boolean => {
  const normalizedName = name.trim().toLowerCase();

  return !groups?.some((group) =>
    group.items.some(
      (item) => item.name.trim().toLowerCase() === normalizedName
    )
  );
};

export const isLastDashboardInUserSection = (
  dashboardItem: DashboardActionTarget,
  groups: DashboardGroupItem[] | undefined
): boolean => {
  if (dashboardItem.section?.kind !== DASHBOARD_SECTION_KINDS.USER) {
    return false;
  }

  const group = groups?.find((item) =>
    item.items.some((dashboard) => dashboard.id === dashboardItem.id)
  );

  return group?.items.length === 1;
};
